import { useMemo, useState } from 'react';

import { ExternalLinkIcon, SearchIcon, StarIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { useAppSelector } from '@/app/hooks';
import {
  useAddLegacyAppFavoriteMutation,
  useGetLegacyAppsFavoritesQuery,
  useGetLegacyAppsQuery,
  useRemoveLegacyAppFavoriteMutation,
} from '@/app/services/legacyApps';
import { Button } from '@/components/ui/button';
import {
  InputGroup,
  InputGroupAddon,
  InputGroupInput,
} from '@/components/ui/input-group';
import { cn } from '@/lib/utils';
import { App } from '@/rtk-query.gql';
import { useToast } from '@/services/toast';

const getDomainName = () =>
  document.cookie
    .split('; ')
    .find((row) => row.startsWith('domainName='))
    ?.split('=')[1] ?? '';

export const LegacyAppsPage = () => {
  const { t } = useTranslation();
  const toast = useToast();
  const { currentConnection } = useAppSelector((state) => state.connections);
  // The connection is only part of the cache key so apps reload after a switch.
  const { data: apps = [], isLoading } = useGetLegacyAppsQuery({
    currentConnection,
  });
  const { data: savedFavorites } = useGetLegacyAppsFavoritesQuery({});
  const [addFavorite] = useAddLegacyAppFavoriteMutation();
  const [removeFavorite] = useRemoveLegacyAppFavoriteMutation();
  const [search, setSearch] = useState('');
  // Optimistic favorites so the star and lists update immediately.
  const [pendingFavorites, setPendingFavorites] = useState<string[] | null>(
    null,
  );
  const favoriteIds = useMemo<string[]>(
    () => pendingFavorites ?? savedFavorites ?? [],
    [pendingFavorites, savedFavorites],
  );

  const toggleFavorite = async (app: App) => {
    const isFavorite = favoriteIds.includes(app.id);
    setPendingFavorites(
      isFavorite
        ? favoriteIds.filter((id) => id !== app.id)
        : [...favoriteIds, app.id],
    );
    try {
      await (isFavorite ? removeFavorite : addFavorite)({
        appId: app.id,
      }).unwrap();
    } catch {
      toast.show({
        severity: 'error',
        summary: t('common.error', 'Error'),
        detail: t(
          'legacyApps.favoriteFailed',
          'Could not update favorites. Please try again.',
        ),
      });
    } finally {
      setPendingFavorites(null);
    }
  };

  const term = search.trim().toLowerCase();
  const { favorites, others } = useMemo(() => {
    const matches = apps.filter(
      (app: App) =>
        !term ||
        app.name?.toLowerCase().includes(term) ||
        app.description?.toLowerCase().includes(term),
    );
    const byId = new Map(matches.map((app: App) => [app.id, app]));
    return {
      // Keep the saved favorites order.
      favorites: favoriteIds
        .map((id) => byId.get(id))
        .filter((app): app is App => Boolean(app)),
      others: matches
        .filter((app: App) => !favoriteIds.includes(app.id))
        .sort((a: App, b: App) => (a.name ?? '').localeCompare(b.name ?? '')),
    };
  }, [apps, favoriteIds, term]);

  const domainName = getDomainName();

  const renderApp = (app: App) => {
    const isFavorite = favoriteIds.includes(app.id);
    return (
      <li
        key={app.id}
        className="group flex items-center gap-1 rounded-lg border border-gray-200 bg-white pr-1 transition-colors hover:border-gray-300 hover:bg-gray-50"
      >
        <a
          href={`${app.url}?username=${domainName}`}
          target="_blank"
          rel="noopener noreferrer"
          className="flex min-w-0 flex-1 items-center gap-2 px-3 py-2 text-sm font-medium text-gray-900 no-underline"
          title={app.description ?? app.name ?? undefined}
        >
          <span className="truncate">{app.name}</span>
          <ExternalLinkIcon className="size-3.5 shrink-0 text-gray-400 opacity-0 transition-opacity group-hover:opacity-100" />
        </a>
        <Button
          variant="ghost"
          size="icon-sm"
          className="shrink-0 bg-transparent"
          aria-pressed={isFavorite}
          aria-label={
            isFavorite
              ? t('legacyApps.removeFavorite', 'Remove from favorites')
              : t('legacyApps.addFavorite', 'Add to favorites')
          }
          onClick={() => toggleFavorite(app)}
        >
          <StarIcon
            className={cn(
              'size-4',
              isFavorite
                ? 'fill-amber-400 text-amber-400'
                : 'text-gray-300 group-hover:text-gray-400',
            )}
          />
        </Button>
      </li>
    );
  };

  const listClass =
    'grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4';

  return (
    <div className="preflight flex flex-col gap-5 p-1">
      <InputGroup className="w-full bg-white sm:max-w-sm">
        <InputGroupAddon>
          <SearchIcon />
        </InputGroupAddon>
        <InputGroupInput
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder={t('legacyApps.search', 'Filter apps...')}
          aria-label={t('legacyApps.search', 'Filter apps...')}
        />
      </InputGroup>

      <section className="flex flex-col gap-2">
        <h2 className="text-sm font-semibold text-gray-700">
          {t('legacyApps.favorites', 'Favorites')}
        </h2>
        {favorites.length ? (
          <ul className={listClass}>{favorites.map(renderApp)}</ul>
        ) : (
          <p className="text-sm text-gray-500">
            {term
              ? t('legacyApps.noMatchingFavorites', 'No matching favorites.')
              : t('legacyApps.noFavorites', 'Star an app to pin it here.')}
          </p>
        )}
      </section>

      <section className="flex flex-col gap-2">
        <h2 className="text-sm font-semibold text-gray-700">
          {t('legacyApps.allApps', 'All apps')}
        </h2>
        {isLoading ? (
          <p className="text-sm text-gray-500">
            {t('common.loading', 'Loading...')}
          </p>
        ) : others.length ? (
          <ul className={listClass}>{others.map(renderApp)}</ul>
        ) : (
          <p className="text-sm text-gray-500">
            {t('legacyApps.noApps', 'No apps found.')}
          </p>
        )}
      </section>
    </div>
  );
};
